import React from 'react';
import { XMarkIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

function Seccion({ titulo, children }) {
    return (
        <section className="space-y-2">
            <h4 className="text-xs font-bold uppercase tracking-wide text-indigo-700 dark:text-indigo-300">{titulo}</h4>
            <div className="text-sm text-slate-700 dark:text-slate-300 space-y-2 leading-relaxed">{children}</div>
        </section>
    );
}

function Termino({ nombre, clase = 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200', children }) {
    return (
        <div className="flex gap-3 items-start">
            <span className={`flex-shrink-0 w-32 text-center px-2 py-0.5 rounded text-[11px] font-semibold ${clase}`}>{nombre}</span>
            <p className="text-sm">{children}</p>
        </div>
    );
}

/** Explicación en lenguaje sencillo del reporte "Tiempo de voz por agente". */
export function AyudaReporteCallCenter({ abierto, onClose }) {
    if (!abierto) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white dark:bg-[#111827] dark:border dark:border-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden"
                onClick={(e) => e.stopPropagation()}>
                <div className="bg-gradient-to-r from-slate-800 to-slate-900 dark:from-[#0D131F] dark:to-[#0D131F] dark:border-b dark:border-slate-800 px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-1.5 bg-indigo-500 rounded-lg"><QuestionMarkCircleIcon className="w-4 h-4 text-white" /></div>
                        <h3 className="text-sm font-bold text-white tracking-wide">¿CÓMO LEER ESTE REPORTE?</h3>
                    </div>
                    <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10" title="Cerrar">
                        <XMarkIcon className="w-5 h-5 text-white" />
                    </button>
                </div>

                <div className="overflow-y-auto p-5 space-y-6">
                    <Seccion titulo="¿Para qué sirve?">
                        <p>
                            Muestra cuánto tiempo pasó cada agente <b>hablando por teléfono</b> con los clientes en las fechas
                            elegidas, y si llegó a la meta diaria que le corresponde según su tramo de cartera.
                        </p>
                        <p>
                            Solo cuenta las llamadas que el agente <b>hace</b> desde su extensión (no las que recibe).
                        </p>
                    </Seccion>

                    <Seccion titulo="Las columnas principales">
                        <Termino nombre="Tramo">Grupo de cartera asignado al agente en ese mes (por días de atraso: 1 a 30, 31 a 60, etc.). Cada tramo tiene su propia meta.</Termino>
                        <Termino nombre="Días">Días en los que el agente hizo al menos una llamada.</Termino>
                        <Termino nombre="Llamadas">Todas las llamadas que marcó, contesten o no.</Termino>
                        <Termino nombre="Contestadas">Llamadas en las que alguien o algo respondió (una persona o un buzón de voz).</Termino>
                        <Termino nombre="Total voz">Suma de todo el tiempo que las llamadas estuvieron conectadas.</Termino>
                        <Termino nombre="Prom. diario">Total de voz dividido para los días trabajados. <b>Es el valor que se compara con la meta.</b></Termino>
                        <Termino nombre="Mínimo / Meta">Tiempo diario esperado para el tramo. El mínimo es lo aceptable; la meta es lo ideal. Los fines de semana tienen valores distintos.</Termino>
                        <Termino nombre="% meta">Qué tanto del objetivo alcanzó. 100% = llegó justo a la meta.</Termino>
                    </Seccion>

                    <Seccion titulo="El estado de cada agente">
                        <Termino nombre="Cumple meta" clase="bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">Su promedio diario llegó o superó la meta.</Termino>
                        <Termino nombre="Sobre el mínimo" clase="bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300">No llegó a la meta, pero sí pasó el mínimo aceptable.</Termino>
                        <Termino nombre="Bajo el mínimo" clase="bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300">Su promedio quedó por debajo del mínimo: necesita atención.</Termino>
                        <Termino nombre="Sin meta">No tiene tramo asignado en ese mes, así que no hay meta con qué comparar.</Termino>
                    </Seccion>

                    <Seccion titulo="¿Cuánto de ese tiempo fue realmente útil?">
                        <p>
                            No todo el tiempo de voz es una conversación con el cliente. Por eso cada llamada se separa así
                            (esta parte es <b>informativa</b>: no cambia el estado ni el % de meta):
                        </p>
                        <Termino nombre="No contestan">El cliente no respondió, el número estaba ocupado o la llamada no salió.</Termino>
                        <Termino nombre="Buzón">Respondió el buzón de voz, una grabación de la operadora (&quot;el número que usted marcó…&quot;) o la llamada quedó en silencio. Se cuentan como contestadas, pero no hubo nadie con quien hablar.</Termino>
                        <Termino nombre="Cortas">Llamadas contestadas que duraron menos de 10 segundos. Son tan breves que no se puede saber qué pasó (por ejemplo, el cliente colgó enseguida).</Termino>
                        <Termino nombre="Sin clasificar">Llamadas contestadas que el sistema todavía no revisa. Es normal que aparezcan en las del día de hoy; en días anteriores deberían ser muy pocas.</Termino>
                        <Termino nombre="Voz efectiva" clase="bg-emerald-50 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">Tiempo en que el agente realmente conversó con una persona.</Termino>
                        <Termino nombre="Tiempo muerto" clase="bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300">Tiempo perdido: lo que sonó el teléfono esperando respuesta (en todas las llamadas) más el tiempo escuchando buzones.</Termino>
                        <Termino nombre="% efect.">Qué parte del tiempo de voz fue conversación real. Mientras más alto, mejor aprovechado está el tiempo.</Termino>
                    </Seccion>

                    <Seccion titulo="Tiempo de voz por día">
                        <p>Tabla con un día por columna, para ver el comportamiento diario de cada agente:</p>
                        <div className="flex flex-wrap gap-2 text-[11px]">
                            <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">Verde: cumplió la meta ese día</span>
                            <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">Amarillo: pasó el mínimo</span>
                            <span className="px-2 py-0.5 rounded bg-rose-50 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300">Rojo: quedó bajo el mínimo</span>
                        </div>
                        <p>Al pasar el mouse sobre un día se ve el detalle: llamadas, buzones, voz efectiva y tiempo muerto.</p>
                    </Seccion>

                    <Seccion titulo="Filtros">
                        <p>
                            Puedes ver un rango de fechas dentro de un mismo mes, un tramo, un agente, una extensión o un supervisor;
                            solo los agentes en un estado; solo días de semana o fines de semana; o contar solo las llamadas contestadas.
                            El botón <b>Excel</b> descarga lo que estás viendo.
                        </p>
                    </Seccion>

                    <Seccion titulo="Ten en cuenta">
                        <ul className="list-disc pl-5 space-y-1">
                            <li>Solo se registran las llamadas de las extensiones nuevas (11xx). Si un agente todavía usa una extensión antigua (2xx), su tiempo aparecerá incompleto.</li>
                            <li>Si una extensión cambió de dueño, las llamadas anteriores aparecen a nombre de la persona que la tiene ahora.</li>
                            <li>&quot;SIN ASIGNAR&quot; significa que la extensión no tiene un agente registrado.</li>
                        </ul>
                    </Seccion>
                </div>
            </div>
        </div>
    );
}
